/**
 * The API client.
 *
 * Every request is relative (`/api/v1/...`) and sends credentials, so the same code works
 * behind Vite's dev proxy and against the API serving the built app from its own origin. There
 * is no base-URL environment variable to get wrong in a deployment.
 *
 * Errors come back from the server in one shape — `{ error, fieldErrors? }` — and are re-thrown
 * as `ApiError`, so a form can show a message at the top and per-field messages underneath
 * without every caller inventing its own parsing.
 */

import { recaptchaHeader, type RecaptchaAction } from "./recaptcha";

const BASE = "/api/v1";

/**
 * Which module the reader is in, sent with every request.
 *
 * The screens the two modules share — clients, payments, expenses, bank accounts, reports — cannot
 * tell from their own address which business is being looked at, and when an organisation has
 * turned sharing off the server has to know in order to answer with the right rows. The rail
 * already works this out (`useActiveModule`); this is how the answer reaches the API.
 *
 * **A header rather than a query parameter on every call.** It applies to every request without
 * fifty call sites remembering it, and it stays out of the URLs that end up in a support ticket.
 * It is a *view* preference and never an authorisation: the org comes from the session as it
 * always did, and a caller naming a module their organisation does not hold is simply ignored.
 */
let currentModule: string | null = null;

export function setRequestModule(module: string | null): void {
  currentModule = module;
}

export class ApiError extends Error {
  readonly status: number;
  readonly fieldErrors?: Record<string, string>;
  /**
   * The shut door this refusal was about, when the server named one.
   *
   * A `402` from the API carries the product's one gate shape on its body — see `assertUnlocked`
   * and `Gating` in `apps/api/src/plan.ts`. Carried through here so a form that has just been
   * refused can offer the padlock and the pricing modal rather than a red box saying no; every
   * other refusal has none and behaves exactly as it did. Deliberately untyped at this layer: the
   * client is the transport, and `Gate` lives with the screens that draw it.
   */
  readonly gate?: unknown;

  constructor(message: string, status: number, fieldErrors?: Record<string, string>, gate?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.gate = gate;
  }
}

/**
 * The endpoints where a `401` is an ordinary answer rather than a lost session.
 *
 * `/auth/me` is asked on every boot and says no to everybody who is not signed in — that is how
 * the door knows to stay shut. The sign-in and second-factor endpoints answer `401` to a wrong
 * password, which is a message for the form, not a reason to reload the form.
 */
const UNAUTHENTICATED_IS_NORMAL = [/^\/auth\/me$/, /^\/auth\/sign-in/, /^\/auth\/two-factor\//, /^\/auth\/sign-up/];

/**
 * A `401` inside the application means the session is gone. Go to the door.
 *
 * **The alternative is what this replaces, and it was worse than it looked.** The rejection became
 * an ordinary query error, and whichever panel had asked painted "Sign in to continue." in the
 * middle of a fully drawn application — rail, header, plan banner and all — under a **Try again**
 * button that could only ever fail the same way. It reads as the product being broken. It is
 * actually the product knowing exactly what is wrong and declining to act on it.
 *
 * The queries retry once before this is reached (`main.tsx`), so a request that arrives here has
 * already been given a second chance and a transient stumble does not throw anybody out.
 *
 * `from` is carried so signing in returns to the screen that was interrupted rather than to the
 * dashboard, and `replace` keeps the dead page out of the back button.
 */
let goingToTheDoor = false;
function sessionIsGone(path: string): void {
  if (typeof window === "undefined") return;
  if (goingToTheDoor) return;
  if (UNAUTHENTICATED_IS_NORMAL.some((allowed) => allowed.test(path))) return;
  // Only from inside the application. The portal signs in its own way and owns its own refusals.
  if (!window.location.pathname.startsWith("/app")) return;

  // Latched, because a screen makes several requests at once and every one of them will fail.
  goingToTheDoor = true;
  const from = window.location.pathname + window.location.search;
  window.location.replace(`/sign-in?from=${encodeURIComponent(from)}`);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path.startsWith("/api") ? path : BASE + path, {
      credentials: "include",
      ...init,
      headers: {
        ...(init?.body ? { "content-type": "application/json" } : {}),
        ...(currentModule ? { "x-rentorng-module": currentModule } : {}),
        ...init?.headers,
      },
    });
  } catch {
    // A network failure is not the same as a rejected request, and saying so saves a support
    // conversation about "the server said no" when the phone simply lost signal.
    throw new ApiError("Could not reach the server. Check your connection and try again.", 0);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    if (response.status === 401) sessionIsGone(path);
    const message =
      (payload as { error?: string } | null)?.error ??
      (response.status === 401
        ? "Your session has expired. Sign in again."
        : "Something went wrong. Nothing was saved.");
    throw new ApiError(
      message,
      response.status,
      (payload as { fieldErrors?: Record<string, string> } | null)?.fieldErrors,
      (payload as { gate?: unknown } | null)?.gate,
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | undefined | null>) =>
    request<T>(path + queryString(params)),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  /**
   * A POST that first asks Google whether this looks like a person.
   *
   * Separate from `post` on purpose. The captcha belongs on the handful of doors anybody can knock
   * on without a session — signing in, signing up, resetting a password, the tenant portal, a
   * payment claim — and putting it on every POST would score somebody once per keystroke of
   * ordinary work while telling Google what they were doing all day.
   *
   * A missing token is not an error here: `recaptchaHeader` returns nothing when the captcha is
   * switched off or Google cannot be reached, the request goes anyway, and the server decides.
   */
  postAsHuman: async <T>(path: string, body: unknown, action: RecaptchaAction) =>
    request<T>(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: await recaptchaHeader(action),
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export function queryString(params?: Record<string, string | number | undefined | null>): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

/** Trigger a CSV download without leaving the page. */
export function downloadCsv(kind: string): void {
  window.location.href = `${BASE}/reports/export/${kind}`;
}
